import { useCallback, useEffect, useRef, useState } from 'react'
import { ConnectionQuality, LocalAudioTrack, Room, RoomEvent, Track } from 'livekit-client'
import { attachPreviewTrack } from './previewAttach'

// LiveKit lifecycle state of the studio room connection.
export const STUDIO_ROOM_STATE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
}

// Whether the stream preview (the adapter's published video) is showing.
export const STUDIO_PREVIEW_STATE = {
  NONE: 'none', // connected, but the ambient-publisher's video hasn't arrived yet
  LIVE: 'live', // preview video subscribed and frames advancing
  NO_VIDEO: 'no-video', // track subscribed but frames stalled (decoder/source froze)
  INTERRUPTED: 'interrupted', // preview track/publisher went away mid-session
}

// The in-pod adapter joins the room under this identity and publishes the
// stream preview (video + audio). See stream-webrtc-adapter/main.go.
const PREVIEW_PUBLISHER_IDENTITY = 'ambient-publisher'
// Commentary on/off rides the data channel under topic "mode"; the adapter
// forwards {slot,on} to its mixer's /mode/<slot>. Single commentator → slot 0.
const MODE_TOPIC = 'mode'
const COMMENTARY_SLOT = 0
// Delay mode:on until the mic audio has reached the mixer (LK→adapter→UDP adds
// ~50-150ms). Ducking the ambient before the mic arrives makes the audience hear
// the duck step against silence — the audible click.
const MODE_ON_GUARD_MS = 400
// Hold the mic track alive after mode:off so the mixer's cross-fade runs against
// continuous audio; stopping the track immediately produces a click.
const MIC_UNPUBLISH_DELAY_MS = 400
// Receiver jitter-buffer target. Enough to absorb ordinary downlink jitter and let a
// retransmission arrive before its frame is due: at 0.1 s a link with ~20 ms jitter froze
// the preview about once a second; at 0.5 s not at all. The mixer measures what the
// commentator actually sees (viewing-pts), so a deeper buffer never moves the voice — it
// only has to stay well inside the audience delay.
const JITTER_BUFFER_TARGET_S = 0.5
// Frame-advance watchdog. The preview track can stay subscribed while frames stop
// (source/decoder stall) → surface that as "no-video". Debounced both ways so it
// doesn't flicker: no painted frame for FRAME_STALL_MS → no-video; it STAYS there
// until frames flow steadily again for FRAME_RECOVER_MS. A gap longer than
// FRAME_FRESH_GAP_MS restarts the "steady" run.
const FRAME_STALL_MS = 2500
const FRAME_RECOVER_MS = 1200
const FRAME_FRESH_GAP_MS = 500
const FRAME_WATCHDOG_INTERVAL_MS = 400

// Non-blocking network indicator for the OPERATOR'S OWN link. "Worst-wins" across
// downlink (preview track loss + RTT), uplink (mic publication loss + RTT), and LiveKit's
// own verdict, then debounced: a new level must persist for NET_HYSTERESIS_TICKS polls
// before it shows, so a transient blip doesn't flap the badge. It rates the operator's
// links only: preview freezes are deliberately NOT an input — a camera, streamer or mixer
// stall freezes the preview too, and used to read "Poor" on a perfect connection. A weak
// downlink still shows (as loss/RTT); a dead one is LiveKit's Reconnecting state.
export const STUDIO_NETWORK_LEVEL = {
  GOOD: 'good',
  WEAK: 'weak',
  BAD: 'bad',
}
const NET_POLL_INTERVAL_MS = 2000
const NET_HYSTERESIS_TICKS = 2

// Re-fetch a fresh join token this long before the current one expires. The token
// TTL is hours, but its clock starts when the page first loads join-info — a tab
// left open past it would drop, so renew ahead of expiry.
const TOKEN_REFRESH_LEAD_MS = 60000

// A/V-sync: the mixer publishes an (rtp_ts ↔ source-pts) anchor on this data topic;
// the browser maps each displayed frame's rtpTimestamp back to source PTS and reports
// the "viewed PTS" back, so the mixer stamps the commentator's mic to the exact frame
// they were watching. Without these reports the mixer uses a static-offset fallback.
const PREVIEW_STATE_TOPIC = 'preview-state'
const VIEWING_PTS_TOPIC = 'viewing-pts'
const VIEWING_PTS_PUBLISH_INTERVAL_MS = 33 // ~30 Hz

// On-site commentary (commentaryMode OnSite): the commentator watches the court, not the
// preview, so the mixer places the voice by when it was spoken instead of by the viewed PTS —
// the same reports as the stream app. The pod's clock is learned over the data channel
// (time-ping → the adapter's time-pong; the shortest round trip gives the best offset), and
// while the mic is live the pod time is sent next to the voice ~30 times a second. Without a
// fresh report the mixer holds the voice back rather than play it out of sync.
const TIME_PING_TOPIC = 'time-ping'
const TIME_PONG_TOPIC = 'time-pong'
const TIME_SYNC_BURST = 5
const TIME_SYNC_BURST_GAP_MS = 200
const TIME_SYNC_INTERVAL_MS = 30000
// Until the first pong: without an offset nothing can be reported.
const TIME_SYNC_RETRY_MS = 1000
const TIME_SYNC_SAMPLE_TTL_MS = 5 * 60 * 1000
const CAPTURE_WALL_TOPIC = 'capture-wall'
const CAPTURE_WALL_INTERVAL_MS = 33

// Mic-delivery feedback: the single answer to "is the commentator being heard right
// now?" The mixer sends a "yes, your audio is reaching the broadcast" confirmation
// ~30 times a second; the browser turns the freshness of those confirmations into one
// of these states (the user never sees any mixer/infra wording). Fail-safe: only show
// "being heard" when there is a fresh, positive confirmation.
export const STUDIO_MIC_DELIVERY = {
  OFF: 'off', // not on air
  CONNECTING: 'connecting', // on air, still waiting for the first confirmation
  LIVE: 'live', // on air AND confirmed: the voice is reaching the broadcast
  NOT_REACHING: 'not_reaching', // on air but the voice is not getting through
}
// How long one "you're being heard" confirmation counts as still valid. Confirmations
// arrive ~30 times a second; if none arrives for this long, assume the voice has
// stopped getting through. Generous on purpose — the channel drops the odd message
// normally, and we don't want the dot to flicker on a single miss.
const HEARD_TIMEOUT_MS = 1000
// When you first go on air, it takes a moment for the mic to start and the mixer to
// confirm. Show "Connecting…" for up to this long; if there's still no confirmation by
// then, show "Not reaching". (Without this, every normal go-on-air would briefly flash
// red before turning green.)
const CONNECTING_TIMEOUT_MS = 3000
// How often the background check re-evaluates which state we're in.
const CHECK_INTERVAL_MS = 250

// Browser-side mic send-gain. A GainNode between the captured mic and the published
// track lets the operator set how loud their voice sits in the broadcast mix. AGC is
// off (it clips word boundaries), so this is the manual replacement. The gain is
// live-adjustable without re-creating the track, so the published SSRC stays constant
// (the mixer locks onto the first SSRC — see startCommentary). 1 = unity (100%); the
// UI runs 0–150% with a 50% default, and warns above 100% (boost amplifies noise).
export const MIC_GAIN_MIN = 0
export const MIC_GAIN_MAX = 1.5
export const MIC_GAIN_DEFAULT = 0.5
const MIC_GAIN_STORAGE_KEY = 'studio.micGain'
const MIC_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: false }

// The gain stage re-publishes a Web Audio-processed track via createMediaStreamDestination.
// Feature-detect the API; if it's missing, fall back to the raw mic (slider hidden).
const gainApiAvailable = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext
  return Boolean(Ctx) && typeof Ctx.prototype?.createMediaStreamDestination === 'function'
}
const readStoredGain = () => {
  try {
    // No stored value (fresh tab) → default. Guard the empty case explicitly: Number(null)
    // and Number('') are 0, which would otherwise pass the range check (MIN is 0) and silence
    // the mic.
    const stored = window.localStorage?.getItem(MIC_GAIN_STORAGE_KEY)
    if (stored == null || stored === '') return MIC_GAIN_DEFAULT
    const raw = Number(stored)
    if (Number.isFinite(raw) && raw >= MIC_GAIN_MIN && raw <= MIC_GAIN_MAX) return raw
  } catch {
    /* localStorage blocked → default */
  }
  return MIC_GAIN_DEFAULT
}

// Encapsulates the commentator's LiveKit session: connect, subscribe to the
// stream preview, publish the mic, and toggle commentary on/off.
// onTokenExpiring is called shortly before the join token expires so the caller
// can re-fetch fresh join info (a new token reconnects the room transparently).
const useStudioRoom = (joinInfo, onTokenExpiring) => {
  const roomRef = useRef(null)
  const micTrackRef = useRef(null)
  const previewVideoTrackRef = useRef(null)
  const previewAudioTrackRef = useRef(null)
  const videoRef = useRef(null)
  // Frame-advance watchdog bookkeeping (see FRAME_STALL_MS).
  const hasVideoTrackRef = useRef(false)
  const lastFrameAtRef = useRef(0)
  const healthySinceRef = useRef(0)
  // Network-quality hysteresis bookkeeping (see STUDIO_NETWORK_LEVEL).
  const networkLevelRef = useRef(STUDIO_NETWORK_LEVEL.GOOD)
  const netPendingRef = useRef({ level: STUDIO_NETWORK_LEVEL.GOOD, count: 0 })
  const netPrevStatsRef = useRef(null)
  // A/V-sync bookkeeping: latest mixer anchor + the viewed PTS of the current frame.
  const rtpAnchorRef = useRef(null)
  const currentViewedPtsRef = useRef(null)
  // Mic-delivery bookkeeping: performance.now() of the last preview-state snapshot that
  // confirmed this commentator's mic was reaching + mixed, and when the current on-air
  // session began (for the startup grace). See the delivery evaluator effect below.
  const lastDeliveredAtRef = useRef(0)
  const onAirSinceRef = useRef(0)
  // Mic send-gain graph: raw capture stream + AudioContext + GainNode feeding the
  // published (processed) track. releaseMicRef holds the latest teardown so the room
  // event handlers (defined in the connect effect) can call it without an effect dep.
  const audioCtxRef = useRef(null)
  const gainNodeRef = useRef(null)
  const rawMicStreamRef = useRef(null)
  const releaseMicRef = useRef(null)
  // The commentary mode as saved (followCommentaryMode; it can be switched while live). On-site:
  // whether the mic is unmuted (capture-wall reports go out only then, kept through the off
  // cross-fade) and pod clock = Date.now() + offset (null until the first pong).
  const [commentaryMode, setCommentaryMode] = useState(joinInfo?.commentaryMode ?? 'OffSite')
  const commentaryModeRef = useRef(commentaryMode)
  const micLiveRef = useRef(false)
  const clockOffsetMsRef = useRef(null)
  const clockSampleRef = useRef(null)
  // Bumped when another commentator goes on air, so a go-on-air in flight doesn't finish.
  const takeoverCountRef = useRef(0)

  const [roomState, setRoomState] = useState(STUDIO_ROOM_STATE.IDLE)
  const [previewState, setPreviewState] = useState(STUDIO_PREVIEW_STATE.NONE)
  const [isOnAir, setIsOnAir] = useState(false)
  // Whether the commentator's voice is actually reaching + being mixed into the
  // broadcast right now (see STUDIO_MIC_DELIVERY). Derived from the mixer's per-slot
  // delivery report + the local on-air intent, debounced in the evaluator effect.
  const [micDelivery, setMicDelivery] = useState(STUDIO_MIC_DELIVERY.OFF)
  // Set when an active on-air session is knocked off involuntarily (e.g. the stream pod
  // restarted), so the UI can prompt the operator to go back on air. Cleared on re-arm.
  const [droppedOffAir, setDroppedOffAir] = useState(false)
  // Set when the stream app went on air and took over the mic. Cleared on re-arm.
  const [takenOffAir, setTakenOffAir] = useState(false)
  // Set when the commentary mode was switched while on air. Cleared on re-arm.
  const [modeChanged, setModeChanged] = useState(false)
  const [networkLevel, setNetworkLevel] = useState(STUDIO_NETWORK_LEVEL.GOOD)
  const [micBusy, setMicBusy] = useState(false)
  const [error, setError] = useState(null)
  // The local mic track, exposed so the UI can render an input-level meter.
  const [micTrack, setMicTrack] = useState(null)
  // Whether the operator has muted the stream preview's audio locally.
  // Preview audio + video share one <video> element so the browser lip-syncs them. That
  // element must stay muted to autoplay, so the preview starts muted; one click on the
  // speaker (a user gesture) unmutes it and audio begins, in sync with the video.
  const [previewMuted, setPreviewMuted] = useState(true)
  const previewMutedRef = useRef(true)
  previewMutedRef.current = previewMuted
  // Browser-side mic send-gain (multiplier; 1 = unity). Persisted across reloads.
  const [micGain, setMicGainState] = useState(readStoredGain)
  const micGainRef = useRef(micGain)
  // Whether the Web Audio gain stage is usable (false → publish raw mic, hide slider).
  const [gainSupported, setGainSupported] = useState(gainApiAvailable)

  // Keep the latest callback without re-arming the expiry timer on every render.
  const onTokenExpiringRef = useRef(onTokenExpiring)
  onTokenExpiringRef.current = onTokenExpiring

  // Taken off air from outside (a takeover, a mode switch); onDropped runs if we were on air.
  const goOffAir = useCallback((onDropped) => {
    takeoverCountRef.current += 1
    micLiveRef.current = false
    setIsOnAir((wasOnAir) => {
      if (wasOnAir) {
        micTrackRef.current?.mute().catch(() => {})
        onDropped()
      }
      return false
    })
  }, [])

  // The mode saved for the stream, pushed over its hub when it is switched while live: the mixer
  // already ended the session on air, so go off air here.
  const followCommentaryMode = useCallback(
    (mode) => {
      if ((mode !== 'OnSite' && mode !== 'OffSite') || mode === commentaryModeRef.current) return
      commentaryModeRef.current = mode
      setCommentaryMode(mode)
      goOffAir(() => setModeChanged(true))
    },
    [goOffAir],
  )

  useEffect(() => {
    if (!joinInfo?.token || !joinInfo?.liveKitUrl) return undefined

    let cancelled = false
    const room = new Room({
      adaptiveStream: false,
      dynacast: false,
      publishDefaults: { dtx: true, red: true, simulcast: false },
    })
    roomRef.current = room

    const capJitterBuffer = (track) => {
      const receiver = track.receiver || (track.mediaStreamTrack && track.mediaStreamTrack.receiver)
      if (!receiver) return
      try {
        receiver.jitterBufferTarget = JITTER_BUFFER_TARGET_S
      } catch {
        /* not supported on this browser */
      }
      try {
        receiver.playoutDelayHint = JITTER_BUFFER_TARGET_S
      } catch {
        /* not supported on this browser */
      }
    }

    // Map a frame's rtpTimestamp back to source PTS via the mixer's latest anchor.
    // Nothing transcodes between mixer and browser, so this is pure arithmetic on
    // the 90 kHz video clock (uint32 wrap-safe; a frame may precede the anchor).
    const rtpViewedPts = (frameRtp) => {
      const anchor = rtpAnchorRef.current
      if (frameRtp == null || !anchor) return null
      let d = (frameRtp - anchor.rtp) >>> 0
      if (d > 0x80000000) d -= 0x100000000
      return anchor.ptsMs + d / 90
    }

    // Watch the preview <video> for frame advancement. requestVideoFrameCallback only
    // records timing; the debounced watchdog (not per-frame) owns the LIVE↔NO_VIDEO
    // flips so a stutter can't flicker the overlay. Commentary is independent of this.
    let frameWatchdog = null
    let rvfcActive = false
    const startFrameWatch = () => {
      const el = videoRef.current
      hasVideoTrackRef.current = true
      const now0 = performance.now()
      lastFrameAtRef.current = now0
      healthySinceRef.current = now0
      // !rvfcActive guards against a second concurrent rVFC loop if TrackSubscribed
      // fires again (reconnect / republish) without an intervening TrackUnsubscribed.
      if (el && typeof el.requestVideoFrameCallback === 'function' && !rvfcActive) {
        rvfcActive = true
        const onFrame = (_now, metadata) => {
          if (!rvfcActive) return
          const now = performance.now()
          // A real gap restarts the "steady frames" run used for recovery.
          if (now - lastFrameAtRef.current > FRAME_FRESH_GAP_MS) healthySinceRef.current = now
          lastFrameAtRef.current = now
          if (metadata && metadata.rtpTimestamp !== undefined) {
            const pts = rtpViewedPts(metadata.rtpTimestamp)
            if (pts !== null) currentViewedPtsRef.current = pts
          }
          el.requestVideoFrameCallback(onFrame)
        }
        el.requestVideoFrameCallback(onFrame)
      }
      if (!frameWatchdog) {
        frameWatchdog = setInterval(() => {
          if (!hasVideoTrackRef.current) return
          const now = performance.now()
          const sinceFrame = now - lastFrameAtRef.current
          if (sinceFrame > FRAME_STALL_MS) {
            // Frames stopped → stalled. Stays here (no recovery below) until steady.
            setPreviewState((p) =>
              p === STUDIO_PREVIEW_STATE.LIVE ? STUDIO_PREVIEW_STATE.NO_VIDEO : p,
            )
          } else if (
            sinceFrame < FRAME_FRESH_GAP_MS &&
            now - healthySinceRef.current >= FRAME_RECOVER_MS
          ) {
            // Frames flowing steadily again → recover.
            setPreviewState((p) =>
              p === STUDIO_PREVIEW_STATE.NO_VIDEO ? STUDIO_PREVIEW_STATE.LIVE : p,
            )
          }
        }, FRAME_WATCHDOG_INTERVAL_MS)
      }
    }
    const stopFrameWatch = () => {
      rvfcActive = false
      hasVideoTrackRef.current = false
      currentViewedPtsRef.current = null
      rtpAnchorRef.current = null
      if (frameWatchdog) {
        clearInterval(frameWatchdog)
        frameWatchdog = null
      }
    }

    // ---- Network quality (worst-wins + hysteresis) -------------------------
    const readNetStats = async () => {
      let rttMs = null
      let lossPct = null
      let upLossPct = null
      let upRttMs = null
      const vTrack = previewVideoTrackRef.current
      if (vTrack?.getRTCStatsReport) {
        const rep = await vTrack.getRTCStatsReport()
        rep?.forEach((s) => {
          if (s.type === 'inbound-rtp' && s.kind === 'video') {
            const now = {
              recv: s.packetsReceived || 0,
              lost: s.packetsLost || 0,
            }
            const prev = netPrevStatsRef.current
            if (prev) {
              const dr = now.recv - prev.recv
              const dl = now.lost - prev.lost
              lossPct = dr + dl > 0 ? (100 * dl) / (dr + dl) : 0
            }
            netPrevStatsRef.current = now
          }
          if (
            s.type === 'candidate-pair' &&
            (s.nominated || s.selected) &&
            s.currentRoundTripTime != null
          ) {
            rttMs = s.currentRoundTripTime * 1000
          }
        })
      }
      const mic = micTrackRef.current
      if (mic?.getRTCStatsReport) {
        const rep = await mic.getRTCStatsReport()
        rep?.forEach((s) => {
          if (s.type === 'remote-inbound-rtp') {
            if (s.fractionLost != null) upLossPct = s.fractionLost * 100
            if (s.roundTripTime != null) upRttMs = s.roundTripTime * 1000
          }
        })
      }
      const cq = roomRef.current?.localParticipant?.connectionQuality
      const worstRtt = Math.max(rttMs || 0, upRttMs || 0)
      const worstLoss = Math.max(lossPct || 0, upLossPct || 0)
      let raw = STUDIO_NETWORK_LEVEL.GOOD
      if (worstRtt > 200 || worstLoss > 2 || cq === ConnectionQuality.Poor) {
        raw = STUDIO_NETWORK_LEVEL.WEAK
      }
      if (worstRtt > 500 || worstLoss > 5 || cq === ConnectionQuality.Lost) {
        raw = STUDIO_NETWORK_LEVEL.BAD
      }
      return raw
    }

    const commitNetLevel = (level) => {
      networkLevelRef.current = level
      netPendingRef.current = { level, count: 0 }
      setNetworkLevel(level)
    }

    const applyRawNetLevel = (raw) => {
      if (raw === networkLevelRef.current) {
        netPendingRef.current = { level: raw, count: 0 }
        return
      }
      const pending = netPendingRef.current
      const count = pending.level === raw ? pending.count + 1 : 1
      if (count >= NET_HYSTERESIS_TICKS) commitNetLevel(raw)
      else netPendingRef.current = { level: raw, count }
    }

    const pollNet = async () => {
      if (roomRef.current?.state !== 'connected') {
        netPrevStatsRef.current = null
        commitNetLevel(STUDIO_NETWORK_LEVEL.GOOD)
        return
      }
      let raw = STUDIO_NETWORK_LEVEL.GOOD
      try {
        raw = await readNetStats()
      } catch {
        /* the indicator must never break the page */
      }
      applyRawNetLevel(raw)
    }
    const netInterval = setInterval(pollNet, NET_POLL_INTERVAL_MS)

    // ---- A/V-sync: publish the viewed PTS at ~30 Hz (unreliable; next supersedes a
    // drop) whenever the preview is producing frames — including BEFORE mic-on. The
    // mixer keeps a 30-entry median of these reports to reject per-report jitter
    // (±1s), so publishing while the operator watches pre-commentary warms that
    // history and the first stamped mic packet is already robust. (pts is null when
    // no frames flow, so an idle tab with no preview still publishes nothing.) Not on-site:
    // there the mixer places the voice by capture-wall and ignores these.
    const viewingPtsInterval = setInterval(() => {
      const pts = currentViewedPtsRef.current
      const r = roomRef.current
      if (commentaryModeRef.current === 'OnSite') return
      if (pts == null || r?.state !== 'connected' || !r.localParticipant) return
      const payload = new TextEncoder().encode(JSON.stringify({ viewing_audio_pts_ms: pts }))
      r.localParticipant
        .publishData(payload, { reliable: false, topic: VIEWING_PTS_TOPIC })
        .catch(() => {})
    }, VIEWING_PTS_PUBLISH_INTERVAL_MS)

    // ---- Clock sync with the pod (both modes, so a switch to on-site can report at once) +
    // on-site capture-wall reports, over this same room (started once connected).
    const syncTimers = []
    let pingId = 0
    const pingBurst = (count) => {
      for (let i = 0; i < count; i += 1) {
        syncTimers.push(
          setTimeout(() => {
            if (room.state !== 'connected') return
            const ping = new TextEncoder().encode(JSON.stringify({ id: ++pingId, t0: Date.now() }))
            room.localParticipant
              .publishData(ping, { reliable: false, topic: TIME_PING_TOPIC })
              .catch(() => {})
          }, i * TIME_SYNC_BURST_GAP_MS),
        )
      }
    }
    let syncInterval = null
    let syncRetryInterval = null
    let captureWallInterval = null
    const startClockSync = () => {
      if (syncInterval) return
      pingBurst(TIME_SYNC_BURST)
      syncInterval = setInterval(() => pingBurst(3), TIME_SYNC_INTERVAL_MS)
      syncRetryInterval = setInterval(() => {
        if (clockOffsetMsRef.current === null) pingBurst(1)
      }, TIME_SYNC_RETRY_MS)
      captureWallInterval = setInterval(() => {
        if (commentaryModeRef.current !== 'OnSite') return
        const offset = clockOffsetMsRef.current
        // Not while reconnecting: they'd only pile up in the channel and arrive late.
        if (offset === null || !micLiveRef.current || room.state !== 'connected') return
        const report = new TextEncoder().encode(JSON.stringify({ wall_ms: Date.now() + offset }))
        room.localParticipant
          .publishData(report, { reliable: false, topic: CAPTURE_WALL_TOPIC })
          .catch(() => {})
      }, CAPTURE_WALL_INTERVAL_MS)
    }
    const onTimePong = (payload) => {
      try {
        const pong = JSON.parse(new TextDecoder().decode(payload))
        const t3 = Date.now()
        const rtt = t3 - pong.t0
        // A clock step mid-ping can make it negative, which would always win below.
        if (rtt <= 0) return
        const best = clockSampleRef.current
        if (!best || rtt <= best.rtt || t3 - best.at > TIME_SYNC_SAMPLE_TTL_MS) {
          clockSampleRef.current = { rtt, at: t3 }
          clockOffsetMsRef.current = pong.server_ms - (pong.t0 + t3) / 2
        }
      } catch {
        /* ignore malformed pongs */
      }
    }

    room
      .on(RoomEvent.Disconnected, () => {
        setRoomState(STUDIO_ROOM_STATE.DISCONNECTED)
        setIsOnAir(false)
        // An involuntary disconnect doesn't reliably stop the local mic, so release it
        // explicitly — otherwise the OS mic indicator stays on while the UI shows
        // "off air" (and the next session would orphan the track + audio graph).
        releaseMicRef.current?.()
      })
      .on(RoomEvent.Reconnecting, () => {
        setRoomState(STUDIO_ROOM_STATE.RECONNECTING)
        // Frames are stalled during the blip — drop a stale LIVE to NO_VIDEO so the
        // pill can't read healthy over a frozen frame; it recovers to LIVE when
        // frames resume (onFrame) or the track re-subscribes.
        setPreviewState((p) =>
          p === STUDIO_PREVIEW_STATE.LIVE ? STUDIO_PREVIEW_STATE.NO_VIDEO : p,
        )
      })
      .on(RoomEvent.Reconnected, () => setRoomState(STUDIO_ROOM_STATE.CONNECTED))
      .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
        if (participant.identity !== PREVIEW_PUBLISHER_IDENTITY) return
        const el = videoRef.current
        if (track.kind === Track.Kind.Video) {
          previewVideoTrackRef.current = track
          // Reassert the mute choice: attaching the video track to the shared element
          // re-runs LiveKit's attachToElement, which unmutes it whenever audio is present.
          if (el) attachPreviewTrack(track, el, previewMutedRef.current)
          capJitterBuffer(track)
          setPreviewState(STUDIO_PREVIEW_STATE.LIVE)
          startFrameWatch()
        } else if (track.kind === Track.Kind.Audio) {
          // Attach the audio to the SAME <video> element as the video so both ride one
          // MediaStream and the browser lip-syncs them. Separate elements drift — the audio
          // jitter buffer grows under a weak network and never drains back, leaving a standing
          // lag even after the connection recovers. The element stays muted until the operator
          // unmutes (a gesture), so audio is silent-but-synced until then.
          previewAudioTrackRef.current = track
          if (el) {
            attachPreviewTrack(track, el, previewMutedRef.current)
            capJitterBuffer(track)
            el.play?.().catch(() => {})
          }
        }
      })
      .on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
        if (participant.identity !== PREVIEW_PUBLISHER_IDENTITY) return
        if (track.kind === Track.Kind.Video) {
          stopFrameWatch()
          track.detach()
          previewVideoTrackRef.current = null
          setPreviewState(STUDIO_PREVIEW_STATE.INTERRUPTED)
        } else if (track.kind === Track.Kind.Audio) {
          track.detach()
          previewAudioTrackRef.current = null
        }
      })
      .on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (participant.identity === PREVIEW_PUBLISHER_IDENTITY) {
          stopFrameWatch()
          previewVideoTrackRef.current = null
          setPreviewState(STUDIO_PREVIEW_STATE.INTERRUPTED)
          // The adapter also forwards the mic to the mixer, so if it leaves (e.g. the stream
          // pod restarted) commentary is no longer reaching air. Pause the mic and drop "on
          // air" so the UI reflects reality; the operator re-arms once the preview returns.
          // If they WERE live, flag it so the UI prompts them — being silently dropped while
          // commentating is the dangerous case.
          micLiveRef.current = false
          micTrackRef.current?.mute().catch(() => {})
          setIsOnAir((wasOnAir) => {
            if (wasOnAir) setDroppedOffAir(true)
            return false
          })
        }
      })
      .on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
        if (topic === TIME_PONG_TOPIC) {
          onTimePong(payload)
          return
        }
        if (topic === MODE_TOPIC) {
          // Another commentator went on air: the adapter now forwards their mic, not ours, so go off
          // air here too. No "mode off": it would be ignored (we're no longer the mic owner). The
          // message is only for the stream app (onsite-… identities).
          const sender = participant?.identity
          if (!sender || sender === PREVIEW_PUBLISHER_IDENTITY) return
          try {
            const obj = JSON.parse(new TextDecoder().decode(payload))
            if (obj.slot !== COMMENTARY_SLOT || obj.on !== true) return
          } catch {
            return
          }
          goOffAir(() => {
            if (sender.startsWith('onsite-')) setTakenOffAir(true)
          })
          return
        }
        if (topic !== PREVIEW_STATE_TOPIC) return
        try {
          const obj = JSON.parse(new TextDecoder().decode(payload))
          // Prefer the adapter's on-the-wire-corrected anchor; fall back to the raw one.
          const rtp = obj.video_rtp_ts_out != null ? obj.video_rtp_ts_out : obj.video_rtp_ts
          if (rtp != null && obj.video_rtp_pts_ms != null) {
            rtpAnchorRef.current = { rtp: rtp >>> 0, ptsMs: obj.video_rtp_pts_ms }
          }
          // Mic-delivery truth for this commentator's slot. "Reaching + mixed" requires
          // the mixer to have this slot on, its RTP arriving (receiving), and the
          // starvation watchdog NOT having silenced it. The evaluator effect below turns
          // the freshness of this confirmation into the user-facing state.
          const slot = Array.isArray(obj.slots) ? obj.slots[COMMENTARY_SLOT] : null
          if (slot && slot.on && slot.receiving && !slot.auto_muted) {
            lastDeliveredAtRef.current = performance.now()
          }
        } catch {
          /* ignore malformed preview-state messages */
        }
      })

    setError(null)
    setRoomState(STUDIO_ROOM_STATE.CONNECTING)
    room
      .connect(joinInfo.liveKitUrl, joinInfo.token)
      .then(() => {
        if (cancelled) return
        setRoomState(STUDIO_ROOM_STATE.CONNECTED)
        startClockSync()
        // Tracks subscribed before connect resolved still need attaching — both ride the
        // one <video> element so the browser keeps them in sync.
        if (videoRef.current) {
          if (previewVideoTrackRef.current) {
            attachPreviewTrack(previewVideoTrackRef.current, videoRef.current, previewMutedRef.current)
          }
          if (previewAudioTrackRef.current) {
            attachPreviewTrack(previewAudioTrackRef.current, videoRef.current, previewMutedRef.current)
            videoRef.current.play?.().catch(() => {})
          }
        }
      })
      .catch((e) => {
        if (cancelled) return
        console.error('Studio room connection failed', e)
        setError('Could not connect to the studio. Please refresh the page.')
        setRoomState(STUDIO_ROOM_STATE.DISCONNECTED)
      })

    return () => {
      cancelled = true
      stopFrameWatch()
      clearInterval(netInterval)
      clearInterval(viewingPtsInterval)
      syncTimers.forEach(clearTimeout)
      if (syncInterval) clearInterval(syncInterval)
      if (syncRetryInterval) clearInterval(syncRetryInterval)
      if (captureWallInterval) clearInterval(captureWallInterval)
      clockOffsetMsRef.current = null
      clockSampleRef.current = null
      netPrevStatsRef.current = null
      room.disconnect()
      roomRef.current = null
      releaseMicRef.current?.()
      previewVideoTrackRef.current = null
      previewAudioTrackRef.current = null
    }
  }, [joinInfo?.token, joinInfo?.liveKitUrl, goOffAir])

  // Renew the token ahead of expiry. A fresh token changes joinInfo.token, which
  // re-runs the connect effect above and reconnects with the new credentials.
  useEffect(() => {
    const expiresAt = joinInfo?.expiresAt
    if (!expiresAt || !onTokenExpiringRef.current) return undefined
    const leadMs = new Date(expiresAt).getTime() - Date.now() - TOKEN_REFRESH_LEAD_MS
    if (leadMs <= 0) {
      onTokenExpiringRef.current()
      return undefined
    }
    const timer = setTimeout(() => onTokenExpiringRef.current?.(), leadMs)
    return () => clearTimeout(timer)
  }, [joinInfo?.expiresAt])

  // Mic-delivery evaluator. Folds the mixer's freshly-confirmed delivery (recorded in
  // lastDeliveredAtRef by the preview-state handler) together with the local on-air
  // intent into the single user-facing state. Re-arms each time on-air flips so a stale
  // confirmation from a previous session can't show a false "being heard".
  useEffect(() => {
    if (!isOnAir) {
      onAirSinceRef.current = 0
      lastDeliveredAtRef.current = 0
      setMicDelivery(STUDIO_MIC_DELIVERY.OFF)
      return undefined
    }
    onAirSinceRef.current = performance.now()
    lastDeliveredAtRef.current = 0 // require a fresh confirmation for THIS on-air session
    setMicDelivery(STUDIO_MIC_DELIVERY.CONNECTING)
    const id = setInterval(() => {
      const now = performance.now()
      const heardRecently =
        lastDeliveredAtRef.current > 0 && now - lastDeliveredAtRef.current <= HEARD_TIMEOUT_MS
      const stillConnecting = now - onAirSinceRef.current < CONNECTING_TIMEOUT_MS
      if (heardRecently) {
        setMicDelivery(STUDIO_MIC_DELIVERY.LIVE)
      } else if (stillConnecting) {
        setMicDelivery(STUDIO_MIC_DELIVERY.CONNECTING)
      } else {
        setMicDelivery(STUDIO_MIC_DELIVERY.NOT_REACHING)
      }
    }, CHECK_INTERVAL_MS)
    return () => clearInterval(id)
  }, [isOnAir])

  const publishMode = useCallback(async (on) => {
    const room = roomRef.current
    if (!room?.localParticipant) return
    const payload = new TextEncoder().encode(JSON.stringify({ slot: COMMENTARY_SLOT, on }))
    await room.localParticipant.publishData(payload, { reliable: true, topic: MODE_TOPIC })
  }, [])

  const startCommentary = useCallback(async () => {
    const room = roomRef.current
    if (!room || room.state !== 'connected') return
    setDroppedOffAir(false)
    setTakenOffAir(false)
    setModeChanged(false)
    const takeoverCount = takeoverCountRef.current
    // This runs inside a click gesture — satisfy browser autoplay so the commentator can
    // hear the stream they're talking over. room.startAudio() force-unmutes every attached
    // audio element, so re-apply the operator's preview-mute choice after it (otherwise the
    // first go-on-air audibly unmutes a preview they had muted, while the UI still shows muted).
    await room.startAudio?.().catch(() => {})
    if (videoRef.current) {
      videoRef.current.muted = previewMutedRef.current
      videoRef.current.play?.().catch(() => {})
    }

    let micTrack = micTrackRef.current
    if (!micTrack) {
      // First time on air: capture + publish the mic ONCE. Every later toggle
      // mutes/unmutes THIS SAME track instead of re-publishing, so its RTP SSRC stays
      // constant. The mixer's per-slot jitter buffer locks onto the first SSRC it sees,
      // so a re-published track (new SSRC) is silently dropped — only the first would
      // ever be heard. noiseSuppression is on (trialled); AGC stays off (it gates audio
      // around perceived silence and clips soft consonants — see the VAD/DTX finding);
      // dtx stays off (publishTrack below). Keep echo cancellation.
      const rawStream = await navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS })
      rawMicStreamRef.current = rawStream
      let trackToPublish = rawStream.getAudioTracks()[0]
      // Insert a GainNode between the captured mic and the published track so the
      // operator can set their level (AGC is off). The gain is adjustable live via
      // setMicGain WITHOUT re-creating the track, so the published SSRC is unchanged.
      // Falls back to the raw mic if Web Audio re-publishing isn't usable (e.g. Safari).
      if (gainApiAvailable()) {
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext
          const ctx = new Ctx({ latencyHint: 'interactive' })
          if (ctx.state === 'suspended') await ctx.resume()
          const source = ctx.createMediaStreamSource(rawStream)
          const gainNode = ctx.createGain()
          gainNode.gain.value = micGainRef.current
          const dest = ctx.createMediaStreamDestination()
          source.connect(gainNode)
          gainNode.connect(dest)
          const processed = dest.stream.getAudioTracks()[0]
          if (!processed) throw new Error('no processed audio track')
          audioCtxRef.current = ctx
          gainNodeRef.current = gainNode
          trackToPublish = processed
          setGainSupported(true)
        } catch {
          // Re-publish path failed → publish the raw mic and hide the slider.
          if (audioCtxRef.current) {
            audioCtxRef.current.close().catch(() => {})
            audioCtxRef.current = null
          }
          gainNodeRef.current = null
          trackToPublish = rawMicStreamRef.current.getAudioTracks()[0]
          setGainSupported(false)
        }
      } else {
        setGainSupported(false)
      }
      micTrack = new LocalAudioTrack(trackToPublish, undefined, true)
      micTrackRef.current = micTrack
      setMicTrack(micTrack)
      await room.localParticipant.publishTrack(micTrack, { dtx: false })
    } else {
      await micTrack.unmute()
    }
    // Another commentator went on air while the mic was being set up: stay off air.
    if (takeoverCountRef.current !== takeoverCount) {
      await micTrack.mute().catch(() => {})
      return
    }
    micLiveRef.current = true
    setIsOnAir(true)
    // Guard before ducking the ambient (see MODE_ON_GUARD_MS).
    await new Promise((resolve) => setTimeout(resolve, MODE_ON_GUARD_MS))
    // Another commentator went on air meanwhile: don't take the mic back from them.
    if (takeoverCountRef.current !== takeoverCount) return
    await publishMode(true)
  }, [publishMode])

  const stopCommentary = useCallback(async () => {
    // Duck the mixer out first; keep the mic publishing through the cross-fade, then
    // MUTE (not unpublish) — muting preserves the publication and SSRC, so the next
    // on-air resumes the exact stream the mixer is already locked onto.
    try {
      await publishMode(false)
    } catch {
      /* keep going even if the data message fails */
    }
    setIsOnAir(false)
    // Let the mixer's cross-fade complete against live audio before muting.
    await new Promise((resolve) => setTimeout(resolve, MIC_UNPUBLISH_DELAY_MS))
    micLiveRef.current = false
    try {
      await micTrackRef.current?.mute()
    } catch {
      /* best-effort */
    }
  }, [publishMode])

  const toggleCommentary = useCallback(async () => {
    if (micBusy) return
    setMicBusy(true)
    try {
      if (isOnAir) await stopCommentary()
      else await startCommentary()
    } catch (e) {
      console.error('Studio microphone error', e)
      setError('Could not access the microphone. Check your browser permissions and try again.')
      setIsOnAir(false)
      // Reset the mic so a failed publish/unmute doesn't poison the next attempt
      // (which would otherwise try to unmute a track that never published).
      releaseMicRef.current?.()
    } finally {
      setMicBusy(false)
    }
  }, [micBusy, isOnAir, startCommentary, stopCommentary])

  // Mute/unmute the stream preview's audio locally (does not affect what the
  // audience hears — only what this commentator hears).
  const togglePreviewMute = useCallback(() => {
    setPreviewMuted((muted) => {
      const next = !muted
      const el = videoRef.current
      if (el) {
        el.muted = next
        // Unmuting is a user gesture — (re)start playback so the audio actually begins.
        if (!next) el.play?.().catch(() => {})
      }
      return next
    })
  }, [])

  // Set the mic send-gain (multiplier). Adjustable live — updates the GainNode in place
  // (no track re-creation, so the SSRC is unchanged) and persists across reloads.
  const setMicGain = useCallback((value) => {
    const clamped = Math.min(MIC_GAIN_MAX, Math.max(MIC_GAIN_MIN, value))
    micGainRef.current = clamped
    setMicGainState(clamped)
    try {
      window.localStorage?.setItem(MIC_GAIN_STORAGE_KEY, String(clamped))
    } catch {
      /* localStorage blocked → in-memory only */
    }
    const node = gainNodeRef.current
    const ctx = audioCtxRef.current
    if (node) {
      // Short ramp to avoid a zipper click on large jumps.
      if (ctx) node.gain.setTargetAtTime(clamped, ctx.currentTime, 0.03)
      else node.gain.value = clamped
    }
  }, [])

  // Stop + release all mic resources (published track, raw capture, gain graph). Held in
  // a ref so the connect effect's room handlers can call it without an effect dependency.
  const releaseMic = useCallback(() => {
    micLiveRef.current = false
    try {
      micTrackRef.current?.stop()
    } catch {
      /* ignore */
    }
    micTrackRef.current = null
    setMicTrack(null)
    try {
      rawMicStreamRef.current?.getTracks().forEach((t) => t.stop())
    } catch {
      /* ignore */
    }
    rawMicStreamRef.current = null
    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect()
      } catch {
        /* ignore */
      }
      gainNodeRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
  }, [])
  releaseMicRef.current = releaseMic

  return {
    videoRef,
    roomState,
    previewState,
    isOnAir,
    micDelivery,
    droppedOffAir,
    takenOffAir,
    modeChanged,
    commentaryMode,
    followCommentaryMode,
    micTrack,
    networkLevel,
    previewMuted,
    micBusy,
    micGain,
    setMicGain,
    gainSupported,
    error,
    toggleCommentary,
    togglePreviewMute,
  }
}

export default useStudioRoom
